import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RadioGroup } from '@headlessui/react';
import { CheckIcon, ClockIcon, DocumentTextIcon, TagIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import cx from 'classnames';

import Button from '~/components/Button';
import Typography from '~/components/Typography';
import useEmail from '~/hooks/useEmail';
import type { Price, PriceInterval, StripeSessions, Tier } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';
import type { RootLoaderData } from '~/root';
import normalizeFontType from '~/utils/normalizeFontType';

import TierPriceBlock from './components/TierPriceBlock';

import PayForm from '../PayForm';
import Badge from './Badge';
import DonationCard from './DonationCard';
import LineItem from './LineItem';

interface Props {
  tier: Tier;
  upgradePage: UpgradePage;
  selectedPriceId: string;
  isExternalCheckoutEnabled: boolean;
  onPriceIdChange: (priceId: string) => void;
  onContinue: () => void;
  isSessionLoading: boolean;
  sessionIds: StripeSessions;
  isCollectingBillingAddress?: boolean;
}

const renderExpiresInXDays = (expiresAt: string) => {
  const expiresAtDate = new Date(expiresAt);
  const today = new Date();
  const timeDiff = expiresAtDate.getTime() - today.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (daysDiff <= 0) {
    return 'Last day to redeem!';
  }

  if (daysDiff === 1) {
    return `Expires in ${daysDiff} day`;
  }

  return `Expires in ${daysDiff} days`;
};

const priceIntervalDisplays: Record<PriceInterval, string> = {
  month: 'upgrade.interval.monthly',
  year: 'upgrade.interval.annually',
  one_time: 'upgrade.interval.one_time',
  donation: '',
};

const TierCard = ({
  tier,
  upgradePage,
  selectedPriceId,
  isExternalCheckoutEnabled = false,
  isCollectingBillingAddress = false,
  onPriceIdChange,
  onContinue,
  // biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
  sessionIds,
  // biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
  isSessionLoading,
}: Props) => {
  const [searchParams] = useSearchParams();
  const isActive = tier.prices.some((price) => price.id === selectedPriceId);
  const isFreePlan = tier.id === 'free';
  const recommendedPrice = tier.prices.find((price) => price.id === upgradePage.main_section.most_popular_price_id);
  const recommendedText = upgradePage.main_section.most_popular_plan_label_text;

  const backgroundColor = upgradePage.main_section.card_bg_color;
  const accentColor = upgradePage.main_section.card_accent_color;
  const borderColor = upgradePage.main_section.card_border_color;
  const fontFamily = normalizeFontType(upgradePage.main_section.card_font_family);
  const cardPriceFontSize = upgradePage.main_section.card_price_font_size;
  const txtColor = upgradePage.main_section.card_text_color;
  const highlightBgColor = upgradePage.main_section.most_popular_plan_bg_color;
  const highlightTextColor = upgradePage.main_section.most_popular_plan_label_color;
  const tabBgColor = upgradePage.main_section.card_tab_bg_color;
  const tabPillBgColor = upgradePage.main_section.card_tab_pill_bg_color;
  const tabTxtColor = upgradePage.main_section.card_tab_text_color;
  const ctaBgColor = upgradePage.main_section.checkout_cta_bg_color;
  const ctaTextColor = upgradePage.main_section.checkout_cta_text_color;

  const priceTabsRef = useRef(null);
  const [selectedPrice, setSelectedPrice] = useState<Price>(recommendedPrice || tier.prices[0]);
  const [email] = useEmail();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const emailInSubscription = subscription?.email;
  const premiumOfferId = searchParams.get('offer_id') || selectedPrice.premium_offer?.id || '';

  const isRecommendedPrice = selectedPrice.id === upgradePage.main_section.most_popular_price_id;

  const handleChangeInTier = (_tierId: string) => {
    onPriceIdChange(selectedPrice.id);
  };

  const handleChangeInPrice = (priceId: string) => {
    const newSelectedPrice = tier.prices.find((price) => price.id === priceId);
    if (newSelectedPrice) {
      setSelectedPrice(newSelectedPrice);
      onPriceIdChange(priceId);
    }
  };

  const cardRequired =
    typeof selectedPrice.premium_offer?.card_required === 'undefined'
      ? true
      : selectedPrice.premium_offer?.card_required;

  const isDonationTier = tier.prices.some((price) => price.interval === 'donation');

  const { t } = useTranslation();

  if (isDonationTier) {
    return (
      <DonationCard
        tier={tier}
        upgradePage={upgradePage}
        selectedPriceId={selectedPriceId}
        isExternalCheckoutEnabled={isExternalCheckoutEnabled}
        onPriceIdChange={onPriceIdChange}
        onContinue={onContinue}
      />
    );
  }

  return (
    <div
      key={tier.id}
      className={cx(
        'relative flex h-full w-72 rounded-lg border bg-white p-6 shadow-sm focus:outline-none lg:w-80 xl:w-96',
        {
          'cursor-pointer': !isExternalCheckoutEnabled,
        },
      )}
      style={{
        backgroundColor,
        border: isActive && !isExternalCheckoutEnabled ? `1px solid ${accentColor}` : `1px solid ${borderColor}`,
      }}
      onClick={(e) => {
        // @ts-expect-error
        if (priceTabsRef.current?.contains(e.target) || priceTabsRef.current === e.target) {
          return;
        }

        handleChangeInTier(tier.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleChangeInTier(tier.id);
        }
      }}
    >
      <div className="flex w-full flex-col space-y-4">
        <Typography size="xl" className="truncate" color={txtColor}>
          {tier.name}
        </Typography>

        {selectedPrice.premium_offer && (
          <Badge
            highlightBgColor={highlightBgColor}
            text={selectedPrice.premium_offer.name}
            fontFamily={fontFamily}
            highlightTextColor={highlightTextColor}
          />
        )}

        {isRecommendedPrice && !selectedPrice.premium_offer && (
          <Badge
            highlightBgColor={highlightBgColor}
            text={recommendedText}
            fontFamily={fontFamily}
            highlightTextColor={highlightTextColor}
          />
        )}

        <TierPriceBlock
          fontFamily={fontFamily}
          priceFontSize={cardPriceFontSize}
          accentColor={accentColor}
          price={selectedPrice}
          txtColor={txtColor}
          isFreePlan={isFreePlan}
          isExternalCheckoutEnabled={isExternalCheckoutEnabled}
        />

        {/* Tier Prices tab (monthly/annual) */}
        {!isFreePlan && tier.prices.length > 1 && (
          <RadioGroup ref={priceTabsRef} value={selectedPrice.id} onChange={handleChangeInPrice}>
            <div className="flex w-full flex-row gap-x-1 rounded-md p-1" style={{ backgroundColor: tabBgColor }}>
              {tier.prices.map((price) => (
                // biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
                <RadioGroup.Option key={price.id} value={price.id} className={({ active }) => cx('w-1/2')}>
                  {({ checked }) => (
                    <div
                      className={cx(
                        'w-full cursor-pointer rounded-md px-2 text-center capitalize shadow-sm',
                        checked && 'border',
                      )}
                      style={{
                        backgroundColor: checked ? tabPillBgColor : undefined,
                        color: checked ? tabTxtColor : tabPillBgColor,
                      }}
                    >
                      <Typography size="xs">{t(priceIntervalDisplays[price.interval])}</Typography>
                    </div>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>
        )}

        {/* Tier Description */}
        <Typography as="span" size="xs" weight="light" fontFamily={fontFamily} color={txtColor} className="break-words">
          {tier.description}
        </Typography>

        {/* Tier Premium Offer */}
        {selectedPrice.premium_offer && (
          <div className="space-y-2">
            {(selectedPrice.premium_offer.custom_terms || selectedPrice.premium_offer.terms) && (
              <LineItem
                accentColor={accentColor}
                fontFamily={fontFamily}
                txtColor={txtColor}
                text={selectedPrice.premium_offer.custom_terms || selectedPrice.premium_offer.terms || ''}
                Icon={TagIcon}
              />
            )}
            {selectedPrice.premium_offer.description && (
              <LineItem
                accentColor={accentColor}
                fontFamily={fontFamily}
                txtColor={txtColor}
                text={selectedPrice.premium_offer.description || ''}
                Icon={DocumentTextIcon}
              />
            )}
            {selectedPrice.premium_offer.expires_at && (
              <LineItem
                accentColor={accentColor}
                fontFamily={fontFamily}
                txtColor={txtColor}
                text={renderExpiresInXDays(selectedPrice.premium_offer.expires_at || '')}
                Icon={ClockIcon}
              />
            )}
          </div>
        )}

        {/* Tier Features */}
        {selectedPrice?.features?.length ? (
          <ul
            style={{
              borderTopColor: accentColor,
            }}
            className={cx({
              'space-y-3 pt-0 text-sm leading-6': true,
              'border-t pt-4': tier.description || selectedPrice.premium_offer,
            })}
          >
            {selectedPrice.features.map((feature: string) => (
              <li key={feature} className="flex gap-x-2">
                <div>
                  {isFreePlan ? (
                    <XMarkIcon className="h-4 w-4 flex-none text-red-500" aria-hidden="true" />
                  ) : (
                    <div
                      style={{
                        color: accentColor,
                      }}
                    >
                      <CheckIcon
                        className={cx({
                          'mt-0.5 h-4 w-4 flex-none': true,
                        })}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                </div>
                <Typography
                  size="xs"
                  weight="light"
                  fontFamily={fontFamily}
                  color={txtColor}
                  className="w-full break-words"
                >
                  {feature}
                </Typography>
              </li>
            ))}
          </ul>
        ) : null}

        {/* Tier Actions */}
        {selectedPrice && isExternalCheckoutEnabled && (
          // biome-ignore lint/complexity/noUselessFragments: Keeping for backwards compatibility
          <>
            {isFreePlan ? (
              <Button
                type="button"
                variant="primary"
                className={cx(fontFamily, 'flex w-full justify-center')}
                onClick={onContinue}
                style={{
                  backgroundColor: ctaBgColor,
                  color: ctaTextColor,
                }}
              >
                {selectedPrice.cta || 'Keep current plan'}
              </Button>
            ) : (
              <PayForm
                text={selectedPrice.cta}
                priceId={selectedPrice.id}
                cardRequired={cardRequired}
                buttonStyle={{
                  backgroundColor: ctaBgColor,
                  color: ctaTextColor,
                }}
                fontFamily={fontFamily}
                premiumOfferId={premiumOfferId}
                email={emailInSubscription || email}
                isCollectingBillingAddress={isCollectingBillingAddress}
                onContinue={onContinue}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TierCard;
